import { useCallback, useMemo } from 'react'

import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import { useOptionalRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { crossesAppBoundary } from 'shared/js/routeOwnership'

/**
 * Returns a predicate for "does navigating to this href leave the app I'm in?",
 * qualified by the destination's per-restaurant page flag.
 *
 * Components should use this rather than calling crossesAppBoundary directly,
 * so the flag and page-context reads live in one place.
 */
export const useCrossAppNavigation = () => {
  const flags = useFlags()
  const restaurantContext = useOptionalRestaurant()

  const additionalPages = useMemo(
    () =>
      restaurantContext?.restaurant?.additionalPages?.map((p) => ({
        route: p.route.startsWith('/') ? p.route : '/' + p.route,
        pageType: p.pageType as string
      })),
    [restaurantContext?.restaurant?.additionalPages]
  )

  return useCallback(
    (href?: string): boolean =>
      !!href && crossesAppBoundary(href, flags, additionalPages ?? undefined),
    [flags, additionalPages]
  )
}

export default useCrossAppNavigation
